/**
 * FeatureTour targetUtils — DOM + geometry helpers for the tour engine.
 *
 * Everything here is deliberately dependency-free and (where possible) pure so
 * the positioning math can be unit tested. jsdom does not implement layout,
 * so tests mock getClientRects()/getBoundingClientRect() where needed.
 */

/** Gap between the spotlight rect and the tooltip card. */
export const CARD_GAP = 12;
/** Minimum distance the card keeps from the viewport edges. */
export const VIEWPORT_MARGIN = 8;

const PLACEMENT_ORDER = ['bottom', 'top', 'right', 'left'];

/**
 * Resolve the CSS selector for a step.
 * `selector` (raw CSS) wins over `target` (a data-tour id).
 */
export function stepSelector(step) {
  if (!step) return null;
  if (step.selector) return step.selector;
  if (step.target) return `[data-tour="${step.target}"]`;
  return null;
}

/**
 * An element counts as visible when it is attached, participates in layout
 * (getClientRects) and is not explicitly hidden. Responsive targets hidden
 * via display={{ base: 'none' }} correctly report as invisible.
 */
export function isElementVisible(el) {
  if (!el || !el.isConnected) return false;
  if (typeof el.getClientRects !== 'function' || el.getClientRects().length === 0) return false;
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const style = window.getComputedStyle(el);
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
  }
  return true;
}

/**
 * First VISIBLE element matching the selector (a data-tour id may legitimately
 * exist twice, e.g. a desktop button plus a mobile menu item).
 */
export function findVisibleTarget(selector) {
  if (!selector) return null;
  let nodes;
  try {
    nodes = document.querySelectorAll(selector);
  } catch (_) {
    return null;
  }
  for (let i = 0; i < nodes.length; i += 1) {
    if (isElementVisible(nodes[i])) return nodes[i];
  }
  return null;
}

/**
 * Resolve when a visible element matching `selector` exists, rejecting after
 * `timeoutMs`. Uses a MutationObserver plus a 100ms poll fallback (covers
 * visibility changes that don't mutate the DOM tree).
 */
export function waitForElement(selector, { timeoutMs = 5000 } = {}) {
  return new Promise((resolve, reject) => {
    const existing = findVisibleTarget(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    let observer = null;
    let pollId = null;
    let timeoutId = null;

    const cleanup = () => {
      if (observer) observer.disconnect();
      if (pollId) clearInterval(pollId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const attempt = () => {
      const el = findVisibleTarget(selector);
      if (el) {
        cleanup();
        resolve(el);
      }
    };

    if (typeof MutationObserver !== 'undefined' && document.body) {
      observer = new MutationObserver(attempt);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    }
    pollId = setInterval(attempt, 100);
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`FeatureTour: target "${selector}" not found/visible within ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

/**
 * Dev-only debugging aid: when a tour target can't be found/visible, dump WHY —
 * how many nodes match, whether each is connected/laid out, which ancestor
 * hides it (display:none / [hidden]), and which tabs are currently selected
 * (tab-switch actions are the most common reason a target stays hidden).
 */
export function diagnoseTarget(selector) {
  let nodes = [];
  try {
    nodes = Array.from(document.querySelectorAll(selector));
  } catch (_) {
    // invalid selector — nodes stays empty
  }
  const details = nodes.map((el) => {
    let hiddenBy = null;
    let node = el;
    while (node && node.nodeType === 1) {
      const s = window.getComputedStyle(node);
      if (s && s.display === 'none') {
        hiddenBy =
          node.tagName.toLowerCase() +
          (node.id ? `#${node.id}` : '') +
          (node.hasAttribute('hidden') ? '[hidden]' : '') +
          ' (display:none)';
        break;
      }
      node = node.parentElement;
    }
    const style = window.getComputedStyle(el);
    return {
      connected: el.isConnected,
      clientRects: typeof el.getClientRects === 'function' ? el.getClientRects().length : 'n/a',
      display: style ? style.display : 'n/a',
      visibility: style ? style.visibility : 'n/a',
      hiddenBy,
    };
  });
  const selectedTabs = Array.from(
    document.querySelectorAll('[role="tab"][aria-selected="true"]')
  ).map((t) => (t.textContent || '').trim());
  // Every tablist on the page with its tab labels and selection state — shows
  // at a glance whether a tab-switch action actually landed.
  const tablists = Array.from(document.querySelectorAll('[role="tablist"]')).map((tl) =>
    Array.from(tl.querySelectorAll('[role="tab"]')).map(
      (t) =>
        `${(t.textContent || '').trim()}${t.getAttribute('aria-selected') === 'true' ? ' [SELECTED]' : ''}`
    )
  );
  // Stringified so a copy-pasted console log carries the full payload.
  console.warn(
    `FeatureTour diagnostics — ${selector}: ${nodes.length} match(es) ` +
      JSON.stringify({ details, tablists }, null, 1)
  );
  return { matches: nodes.length, details, selectedTabs, tablists };
}

/** Expand a rect outward by `padding` px on every side. */
export function padRect(rect, padding = 0) {
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

/** True when two rects match within `epsilon` px on every dimension. */
export function rectsRoughlyEqual(a, b, epsilon = 0.5) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < epsilon &&
    Math.abs(a.left - b.left) < epsilon &&
    Math.abs(a.width - b.width) < epsilon &&
    Math.abs(a.height - b.height) < epsilon
  );
}

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Scroll the element into view (if needed), then wait until its rect stops
 * moving — this also absorbs entrance animations (e.g. Chakra modal scale-in)
 * so the spotlight is measured against the settled position.
 */
export function scrollToTarget(el, { maxWaitMs = 800 } = {}) {
  return new Promise((resolve) => {
    if (!el || typeof el.getBoundingClientRect !== 'function') {
      resolve();
      return;
    }

    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const rect = el.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= viewportH;
    if (!fullyVisible && typeof el.scrollIntoView === 'function') {
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (_) {
        el.scrollIntoView();
      }
    }

    if (typeof requestAnimationFrame !== 'function') {
      resolve();
      return;
    }

    // Settle loop: resolve once the rect is stable for 2 consecutive frames.
    let last = el.getBoundingClientRect();
    let stableFrames = 0;
    const startedAt = Date.now();
    const tick = () => {
      const now = el.getBoundingClientRect();
      const stable = Math.abs(now.top - last.top) < 0.5 && Math.abs(now.left - last.left) < 0.5;
      stableFrames = stable ? stableFrames + 1 : 0;
      last = now;
      if (stableFrames >= 2 || Date.now() - startedAt > maxWaitMs) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function positionFor(placement, targetRect, cardSize) {
  const centerX = targetRect.left + targetRect.width / 2;
  const centerY = targetRect.top + targetRect.height / 2;
  switch (placement) {
    case 'top':
      return { top: targetRect.top - CARD_GAP - cardSize.height, left: centerX - cardSize.width / 2 };
    case 'right':
      return { top: centerY - cardSize.height / 2, left: targetRect.left + targetRect.width + CARD_GAP };
    case 'left':
      return { top: centerY - cardSize.height / 2, left: targetRect.left - CARD_GAP - cardSize.width };
    case 'bottom':
    default:
      return { top: targetRect.top + targetRect.height + CARD_GAP, left: centerX - cardSize.width / 2 };
  }
}

/**
 * A placement "fits" when the card fully fits on its primary axis; the cross
 * axis is later clamped into the viewport (flip + shift, like floating-ui).
 */
function placementFits(placement, targetRect, cardSize, viewport) {
  switch (placement) {
    case 'bottom':
      return targetRect.top + targetRect.height + CARD_GAP + cardSize.height <= viewport.height - VIEWPORT_MARGIN;
    case 'top':
      return targetRect.top - CARD_GAP - cardSize.height >= VIEWPORT_MARGIN;
    case 'right':
      return targetRect.left + targetRect.width + CARD_GAP + cardSize.width <= viewport.width - VIEWPORT_MARGIN;
    case 'left':
      return targetRect.left - CARD_GAP - cardSize.width >= VIEWPORT_MARGIN;
    default:
      return false;
  }
}

/**
 * Compute a fixed-position for the tooltip card next to the (padded) target
 * rect. Tries the preferred placement first, flips through the others, and
 * always clamps into the viewport with VIEWPORT_MARGIN.
 *
 * @returns {{ top: number, left: number, placement: string }}
 */
export function computeCardPosition(targetRect, cardSize, viewport, preferred = 'auto') {
  const candidates =
    preferred && preferred !== 'auto'
      ? [preferred, ...PLACEMENT_ORDER.filter((p) => p !== preferred)]
      : PLACEMENT_ORDER;

  let placement = candidates.find((p) => placementFits(p, targetRect, cardSize, viewport));
  if (!placement) placement = 'bottom'; // degenerate (huge target): clamp below

  const raw = positionFor(placement, targetRect, cardSize);
  return {
    placement,
    top: clamp(raw.top, VIEWPORT_MARGIN, viewport.height - cardSize.height - VIEWPORT_MARGIN),
    left: clamp(raw.left, VIEWPORT_MARGIN, viewport.width - cardSize.width - VIEWPORT_MARGIN),
  };
}
